// transcriptionService.ts

import { getAuth } from 'firebase/auth';
import transcriptionJobsStore from '../stores/TranscriptionJobsStore';
import type { SttPresetId, SttProviderId } from '../types/stt';

export interface TranscriptionParams {
    fileName: string;
    recordingId: string;
    transcriptionText?: string;
    timingData?: any;
    languageCode?: string;
    bypassModeration?: boolean;
    /** Single speaker (default) or multiple — enables diarisation on the service when `multiple`. */
    speakerMode?: 'single' | 'multiple';
    /** Super-admin request override (A) or client echo of saved recording prefs (B). Server validates. */
    sttProvider?: SttProviderId;
    sttPreset?: SttPresetId;
}

const TRANSCRIPTION_URL = import.meta.env.VITE_TRANSCRIPTION_URL
    || 'https://transcription-service-316383840582.europe-west2.run.app/speech-to-text';

function makeJobId(): string {
    return `tx-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const fetchWithRetry = async (
    url: string, 
    getOptions: () => Promise<RequestInit>, 
    retries: number = 5
): Promise<Response> => {
    for (let i = 0; i < retries; i++) {
        try {
            // Exponential backoff: 2s, 4s, 8s, 16s, 32s
            const delay = i === 0 ? 2000 : Math.min(32000, Math.pow(2, i) * 2000);
            
            if (i > 0) {
                await new Promise(res => setTimeout(res, delay));
            }

            // Get fresh options (including token) for each attempt
            const options = await getOptions();
            const response = await fetch(url, options);
            if (response.ok) {
                return response;
            }
            const errorText = await response.text();
            console.error(`Attempt ${i + 1} failed: ${response.status} ${response.statusText}`, errorText);
            
            // Check for specific "No document to update" error
            if (errorText.includes('No document to update') && i < retries - 1) {
                continue;
            }
            
            // If it's a 404 or 503, likely cold start issue
            if ((response.status === 404 || response.status === 503) && i < retries - 1) {
                continue;
            }
            
            // If it's a 401, the token will be refreshed in getOptions on next attempt
            if (response.status === 401 && i < retries - 1) {
                continue;
            }
            
            if (
                errorText.includes('CONVERSION_FAILED') ||
                errorText.includes('does not appear to contain audio') ||
                errorText.includes('Transcoding failed')
            ) {
                throw new Error(
                    'This audio file could not be converted for transcription. M4A/ALAC files are re-encoded on the server; if this persists, re-export the recording as MP3 or AAC and upload again.'
                );
            }

            // For other errors, might want to fail fast
            if (response.status !== 404 && response.status !== 503 && response.status !== 401) {
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
        } catch (error) {
            console.error(`Attempt ${i + 1} failed:`, error);
            if (i < retries - 1) {
                continue;
            }
            throw error;
        }
    }
    throw new Error(`Failed to fetch ${url} after ${retries} retries`);
};

/** Performs the HTTP transcription request (used by the job queue). */
async function executeTranscriptionRequest({
    fileName,
    recordingId,
    transcriptionText,
    timingData,
    languageCode = 'en-GB',
    bypassModeration = false,
    speakerMode = 'single',
    sttProvider,
    sttPreset,
}: TranscriptionParams): Promise<any> {
    if (!fileName) {
        throw new Error('Recording path is undefined');
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
        throw new Error('No authenticated user found');
    }

    const getRequestOptions = async (): Promise<RequestInit> => {
        const idToken = await user.getIdToken(true);
        return {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({
                fileName,
                recordingId,
                transcriptionText,
                timingData,
                languageCode,
                bypassModeration,
                speakerMode: speakerMode === 'multiple' ? 'multiple' : 'single',
                sttProvider,
                sttPreset,
            }),
        };
    };

    const transcriptionResponse = await fetchWithRetry(TRANSCRIPTION_URL, getRequestOptions);

    if (!transcriptionResponse.ok) {
        const errorText = await transcriptionResponse.text();
        console.error('Transcription service error:', transcriptionResponse.status, errorText);

        if (transcriptionResponse.status === 401) {
            throw new Error('Authentication failed. Please refresh the page and try again. If the issue persists, the transcription service may need to be configured to accept Firebase ID tokens.');
        }

        if (errorText.includes('No document to update')) {
            throw new Error(`Recording document not found. The recording may still be being saved. Please try again in a few moments.`);
        }

        if (errorText.includes('language') && errorText.includes('not supported')) {
            throw new Error(`The selected language (${languageCode}) is not fully supported. Please try using 'en-GB' (UK English) or 'en-US' (US English) instead.`);
        }

        if (errorText.includes('CODEC_NOT_SUPPORTED')) {
            throw new Error('The audio file format is not supported. Please try converting it to a different format.');
        }

        if (
            errorText.includes('CONVERSION_FAILED') ||
            errorText.includes('does not appear to contain audio') ||
            errorText.includes('Transcoding failed')
        ) {
            throw new Error(
                'This audio file could not be converted for transcription. M4A/ALAC files are re-encoded on the server; if this persists, re-export the recording as MP3 or AAC and upload again.'
            );
        }

        throw new Error(`HTTP error! status: ${transcriptionResponse.status}, message: ${errorText}`);
    }

    return transcriptionResponse.json();
}

export type TranscriptionJobHandlers = {
    onSuccess?: (data: unknown) => void;
    onError?: (error: unknown) => void;
};

/**
 * Starts transcription in the background and updates {@link transcriptionJobsStore} for the footer progress bar.
 * @returns job id
 */
export function queueTranscription(
    params: TranscriptionParams,
    handlers?: TranscriptionJobHandlers
): string {
    const jobId = makeJobId();
    transcriptionJobsStore.beginJob(jobId, params.recordingId);
    void (async () => {
        try {
            const data = await executeTranscriptionRequest(params);
            handlers?.onSuccess?.(data);
        } catch (error) {
            console.error('Error initiating transcription:', error);
            handlers?.onError?.(error);
        } finally {
            transcriptionJobsStore.endJob(jobId);
        }
    })();
    return jobId;
}

/** Same HTTP work as {@link queueTranscription}, but returns a Promise (still tracked in the job store). */
export const initiateTranscription = (params: TranscriptionParams): Promise<any> =>
    new Promise((resolve, reject) => {
        queueTranscription(params, {
            onSuccess: resolve,
            onError: reject,
        });
    });
